import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PageItem } from 'src/app/shared/interfaces/page-item.interface';
import {
  GestaoMudanca,
  GestaoMudancaCreate,
  GestaoMudancaIndicadores,
  GestaoMudancaLookups,
  GestaoMudancaPlanoAcao,
  GestaoMudancaPlanoAcaoCreate,
  GestaoMudancaPlanoAcaoHistorico,
  GestaoMudancaPendencia,
  GestaoMudancaPendenciaValidacao,
  GestaoMudancaPendenciaContador
} from 'src/app/shared/interfaces/gestao-mudanca/gestao-mudanca.interface';

@Injectable({ providedIn: 'root' })
export class GestaoMudancaAPIService {

  private baseUrl = `${environment.APIEndpoint}gestao-mudanca`;

  constructor(private http: HttpClient) {}

  // ── Lookups / Indicadores ───────────────────────────────────────
  getLookups(): Observable<GestaoMudancaLookups> {
    return this.http.get<GestaoMudancaLookups>(`${this.baseUrl}/lookup`);
  }

  getIndicadores(): Observable<GestaoMudancaIndicadores> {
    return this.http.get<GestaoMudancaIndicadores>(`${this.baseUrl}/indicadores`);
  }

  // ── Registro ────────────────────────────────────────────────────
  getAll(
    pageNumber: number = 1,
    pageSize: number = 15,
    busca: string = '',
    statusId: number | null = null,
    areaId: number | null = null,
    normaId: number | null = null,
    parecerId: number | null = null,
    dataAberturaInicio: string = '',
    dataAberturaFim: string = '',
    ativo: boolean | null = null
  ): Observable<PageItem> {
    let params = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (busca)               params += `&busca=${busca}`;
    if (statusId)            params += `&statusId=${statusId}`;
    if (areaId)              params += `&areaId=${areaId}`;
    if (normaId)             params += `&normaId=${normaId}`;
    if (parecerId)           params += `&parecerId=${parecerId}`;
    if (dataAberturaInicio)  params += `&dataAberturaInicio=${dataAberturaInicio}`;
    if (dataAberturaFim)     params += `&dataAberturaFim=${dataAberturaFim}`;
    if (ativo !== null)      params += `&ativo=${ativo}`;
    return this.http.get<PageItem>(`${this.baseUrl}/listar-paginada${params}`);
  }

  getById(id: number): Observable<GestaoMudanca> {
    return this.http.get<GestaoMudanca>(`${this.baseUrl}/${id}`);
  }

  create(dto: GestaoMudancaCreate): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}`, dto);
  }

  update(id: number, dto: GestaoMudancaCreate): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}`, dto);
  }

  alterarStatus(id: number, statusId: number): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/${id}/status/${statusId}`, {});
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  // ── Plano de Ação ───────────────────────────────────────────────
  listarPlanoAcao(gestaoMudancaId: number): Observable<GestaoMudancaPlanoAcao[]> {
    return this.http.get<GestaoMudancaPlanoAcao[]>(`${this.baseUrl}/${gestaoMudancaId}/plano-acao`);
  }

  obterPlanoAcao(id: number): Observable<GestaoMudancaPlanoAcao> {
    return this.http.get<GestaoMudancaPlanoAcao>(`${this.baseUrl}/plano-acao/${id}`);
  }

  listarPlanoAcaoHistorico(id: number): Observable<GestaoMudancaPlanoAcaoHistorico[]> {
    return this.http.get<GestaoMudancaPlanoAcaoHistorico[]>(`${this.baseUrl}/plano-acao/${id}/historico`);
  }

  criarPlanoAcao(dto: GestaoMudancaPlanoAcaoCreate): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/plano-acao`, dto);
  }

  atualizarPlanoAcao(id: number, dto: GestaoMudancaPlanoAcaoCreate): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/plano-acao/${id}`, dto);
  }

  movimentarPlanoAcao(id: number, statusId: number, observacao: string): Observable<void> {
    return this.http.patch<void>(`${this.baseUrl}/plano-acao/${id}/movimentar`, { statusId, observacao });
  }

  deletarPlanoAcao(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/plano-acao/${id}`);
  }

  // ── Transições autorizadas (responsável / aprovador) ────────────────
  alterarStatusPlanoAcao(id: number, statusId: number, observacao: string = ''): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/plano-acao/${id}/alterar-status`, { statusId, observacao });
  }
  enviarValidacaoPlanoAcao(id: number, observacao: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/plano-acao/${id}/enviar-validacao`, { observacao });
  }
  aprovarPlanoAcao(id: number, observacao: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/plano-acao/${id}/aprovar`, { observacao });
  }
  reprovarPlanoAcao(id: number, observacao: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/plano-acao/${id}/reprovar`, { observacao });
  }
  reabrirPlanoAcao(id: number, observacao: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/plano-acao/${id}/reabrir`, { observacao });
  }
  aprovarInicioPlanoAcao(id: number, observacao: string = ''): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/plano-acao/${id}/aprovar-inicio`, { observacao });
  }
  reprovarInicioPlanoAcao(id: number, observacao: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/plano-acao/${id}/reprovar-inicio`, { observacao });
  }

  // ── Pendências ──────────────────────────────────────────────────────
  obterMinhasPendencias(usuarioId: number): Observable<GestaoMudancaPendencia[]> {
    return this.http.get<GestaoMudancaPendencia[]>(`${this.baseUrl}/minhas-pendencias/${usuarioId}`);
  }
  getPendenciasValidacao(): Observable<GestaoMudancaPendenciaValidacao[]> {
    return this.http.get<GestaoMudancaPendenciaValidacao[]>(`${this.baseUrl}/pendencias-validacao`);
  }
  getPendenciasAprovacaoInicial(): Observable<GestaoMudancaPendenciaValidacao[]> {
    return this.http.get<GestaoMudancaPendenciaValidacao[]>(`${this.baseUrl}/pendencias-aprovacao-inicial`);
  }
  getContadorPendencias(): Observable<GestaoMudancaPendenciaContador> {
    return this.http.get<GestaoMudancaPendenciaContador>(`${this.baseUrl}/pendencias/contador`);
  }
}
